import { useEffect } from "react";
import { useFieldArray, useFormContext, Control } from "react-hook-form";
import MembershipTiersTable from "./academic/MembershipTiersTable";
import MembershipTierSelect from "./academic/MembershipTierSelect";
import DegreeFields from "./academic/DegreeFields";
import { Button } from "@/components/ui/button";
import {
  MemberProfileFormType,
  DegreeFormType,
  ApplicationFormType,
} from "@/lib/form-schemas";
import { Trash } from "lucide-react";

interface AcademicFieldsProps {
  control: Control<ApplicationFormType>;
  tierValue: string | undefined;
  onTierChange: (value: string) => void;
}

const AcademicFields = ({
  control,
  tierValue,
  onTierChange,
}: AcademicFieldsProps) => {
  const { watch, formState } = useFormContext<MemberProfileFormType>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "degrees",
  });

  // Optionally, auto-append a degree if none exist
  useEffect(() => {
    if (fields.length === 0) {
      append({
        degreeLevel: "HighSchool",
        institution: "",
        graduationYear: 2025,
        gpa: "",
        degree_title: "High-School Diploma",
      });
    }
  }, []);

  // Optionally, show a message if the selected tier requires a certain degree type
  const degrees: DegreeFormType[] = watch("degrees");

  return (
    <div className="space-y-6">
      <MembershipTiersTable />

      <MembershipTierSelect value={tierValue} onValueChange={onTierChange} />

      <div className="space-y-6">
        {fields.map((field, index) => (
          <div
            key={field.id}
            className="relative border border-neutral-200 rounded-lg p-4 mb-2"
          >
            <DegreeFields
              index={index}
              control={control}
              degreeId={field.id}
              hasOtherHighSchool={degrees?.some(
                (d, i) => d.degreeLevel === "HighSchool" && i !== index
              )}
            />
            {field.degreeLevel !== "HighSchool" && (
              <Button
                type="button"
                variant="destructive"
                className="absolute top-2 right-2"
                onClick={() => remove(index)}
                disabled={fields.length === 1}
              >
                <Trash />
              </Button>
            )}
          </div>
        ))}
        {formState.errors.degrees?.root?.message && (
          <p className="text-sm font-medium text-destructive text-center mt-2">
            {formState.errors.degrees.root.message}
          </p>
        )}
        <div className="flex justify-center">
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              append({
                degreeLevel: "Bachelors",
                institution: "",
                major: "",
                graduationYear: 2025,
                gpa: "",
                degree_title: "",
              })
            }
          >
            Add Degree
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AcademicFields;
